// Fullscreen Gallery JavaScript
class FullscreenGallery {
  constructor() {
    this.currentIndex = 0;
    this.mediaItems = [];
    this.overlay = null;
    this.mainImage = null;
    this.thumbnailsContainer = null;
    this.counter = null;
    this.isKeyboardNav = false;

    this.init();
  }

  init() {
    // Create overlay structure
    this.createOverlay();

    // Bind events
    this.bindEvents();

    // Collect media items
    this.collectMediaItems();

    // Initialize thumbnails
    this.initializeThumbnails();
  }

  createOverlay() {
    this.overlay = document.createElement("div");
    this.overlay.className = "fullscreen-gallery-overlay";

    this.overlay.innerHTML = `
      <div class="fullscreen-gallery-container">
        <div class="fullscreen-main-image">
          <img src="" alt="" />
        </div>
        <div class="fullscreen-thumbnails"></div>
        
        <button class="fullscreen-nav prev" aria-label="Previous image">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="15,18 9,12 15,6"></polyline>
          </svg>
        </button>
        
        <button class="fullscreen-nav next" aria-label="Next image">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9,18 15,12 9,6"></polyline>
          </svg>
        </button>
        
        <button class="fullscreen-close" aria-label="Close gallery">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        <div class="fullscreen-counter">
          <span class="current">1</span> / <span class="total">1</span>
        </div>
      </div>
    `;

    document.body.appendChild(this.overlay);

    // Get references
    this.mainImage = this.overlay.querySelector(".fullscreen-main-image img");
    this.thumbnailsContainer = this.overlay.querySelector(
      ".fullscreen-thumbnails"
    );
    this.counter = this.overlay.querySelector(".fullscreen-counter");
  }

  collectMediaItems() {
    this.mediaItems = [];

    // Prefer hidden .product-media-gallery if present (for all media types)
    const gallery = document.querySelector(".product-media-gallery");
    let mediaElements = [];
    if (gallery) {
      mediaElements = Array.from(
        gallery.querySelectorAll("img, video, iframe")
      );
    } else {
      // Fallback to visible gallery
      mediaElements = Array.from(
        document.querySelectorAll(
          ".product-image-magnifier-img, .product__media-item img, .product__media-item video, .product__media-external-video"
        )
      );
    }

    mediaElements.forEach((media, index) => {
      let item = {};
      if (media.tagName.toLowerCase() === "video") {
        // Video element: get all sources, poster, and type
        let sources = [];
        media.querySelectorAll("source").forEach((source) => {
          sources.push({
            src: source.src,
            type: source.type || "",
          });
        });
        // Prefer .mp4 source if available, else fallback to first
        let videoSrc = "";
        if (sources.length > 0) {
          const mp4 = sources.find(
            (s) => s.type.includes("mp4") || s.src.endsWith(".mp4")
          );
          videoSrc = mp4 ? mp4.src : sources[0].src;
        } else {
          videoSrc = media.currentSrc || media.src;
        }
        item = {
          type: "video",
          src: videoSrc,
          sources: sources,
          alt:
            media.getAttribute("aria-label") ||
            media.getAttribute("alt") ||
            `Product video ${index + 1}`,
          thumbnail: media.poster || media.getAttribute("data-poster") || "",
        };
      } else if (media.tagName.toLowerCase() === "iframe") {
        // External video (YouTube, Vimeo)
        item = {
          type: "external_video",
          src: media.src,
          alt: media.getAttribute("title") || `Product video ${index + 1}`,
          thumbnail: "", // Could be improved if you have a preview image
        };
      } else {
        // Image element
        item = {
          type: "img",
          src: media.src || media.querySelector("source")?.src,
          alt: media.alt || `Product image ${index + 1}`,
          thumbnail:
            media.src || media.poster || media.querySelector("source")?.src,
        };
        // For responsive images, get the highest quality source
        if (media.srcset) {
          const sources = media.srcset.split(",");
          const highestRes = sources[sources.length - 1].trim().split(" ")[0];
          item.src = highestRes;
        }
      }
      this.mediaItems.push(item);
    });

    // Fallback: collect from thumbnail list if main gallery not found
    if (this.mediaItems.length === 0) {
      const thumbnails = document.querySelectorAll(".thumbnail-list__item img");
      thumbnails.forEach((thumb, index) => {
        this.mediaItems.push({
          type: "img",
          src: thumb.src
            .replace("_small", "")
            .replace("_thumb", "")
            .replace("_compact", ""),
          alt: thumb.alt || `Product image ${index + 1}`,
          thumbnail: thumb.src,
        });
      });
    }
  }

  initializeThumbnails() {
    this.thumbnailsContainer.innerHTML = "";

    // Store references for tap detection
    this._thumbTapData = { startX: 0, startY: 0, moved: false, target: null };

    this.mediaItems.forEach((item, index) => {
      const thumbnail = document.createElement("div");
      thumbnail.className = "fullscreen-thumbnail";
      if (item.type === "video") {
        thumbnail.classList.add("video");
        if (item.thumbnail) {
          thumbnail.innerHTML = `<img src="${item.thumbnail}" alt="${item.alt}" />`;
        } else {
          thumbnail.innerHTML = `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#222;color:#fff;font-size:2em;">▶</div>`;
        }
      } else {
        thumbnail.innerHTML = `<img src="${item.thumbnail}" alt="${item.alt}" />`;
      }

      // Mouse click for desktop
      thumbnail.addEventListener("click", (e) => {
        // On touch devices, ignore click if it was a scroll (handled below)
        if (
          window.matchMedia &&
          window.matchMedia("(pointer: coarse)").matches &&
          this._thumbTapData.moved
        )
          return;
        this.goToSlide(index);
      });

      // Store index for tap detection
      thumbnail.dataset.index = index;

      this.thumbnailsContainer.appendChild(thumbnail);
    });

    // Touch/scroll handling for mobile: only trigger goToSlide if not a scroll
    const container = this.thumbnailsContainer;
    let tapData = this._thumbTapData;
    container.addEventListener("touchstart", (e) => {
      if (e.touches.length === 1) {
        tapData.startX = e.touches[0].clientX;
        tapData.startY = e.touches[0].clientY;
        tapData.moved = false;
        tapData.target = e.target.closest(".fullscreen-thumbnail");
      }
    });
    container.addEventListener("touchmove", (e) => {
      if (e.touches.length === 1) {
        const dx = Math.abs(e.touches[0].clientX - tapData.startX);
        const dy = Math.abs(e.touches[0].clientY - tapData.startY);
        if (dx > 10 || dy > 10) {
          tapData.moved = true;
        }
      }
    });
    container.addEventListener("touchend", (e) => {
      if (!tapData.moved && tapData.target) {
        // Only trigger if touchend is on the same thumbnail
        const endTarget = e.changedTouches[0].target.closest(
          ".fullscreen-thumbnail"
        );
        if (endTarget === tapData.target) {
          const idx = parseInt(tapData.target.dataset.index, 10);
          if (!isNaN(idx)) this.goToSlide(idx);
        }
      }
      tapData.moved = false;
      tapData.target = null;
    });

    // Update counter
    this.counter.querySelector(".total").textContent = this.mediaItems.length;
  }

  bindEvents() {
    // Close button
    document.addEventListener("click", (e) => {
      if (e.target.closest(".fullscreen-close")) {
        this.close();
      }
    });

    // Navigation buttons
    document.addEventListener("click", (e) => {
      if (e.target.closest(".fullscreen-nav.prev")) {
        this.previousSlide();
      } else if (e.target.closest(".fullscreen-nav.next")) {
        this.nextSlide();
      }
    });

    // Click on main thumbnails to open gallery
    document.addEventListener("click", (e) => {
      const thumbnail = e.target.closest(
        ".thumbnail-list__item img, .product-image-magnifier-img"
      );
      if (thumbnail) {
        const index = this.findMediaIndex(thumbnail);
        this.open(index);
      }
    });

    // Simple click handler for product images - always works
    document.addEventListener("click", (e) => {
      const magnifierImg =
        e.target.closest(".product-image-magnifier-img") ||
        e.target.matches(".product-image-magnifier-img")
          ? e.target
          : null;

      if (magnifierImg) {
        e.preventDefault();
        e.stopPropagation();
        const index = this.findMediaIndex(magnifierImg);
        this.open(index);
        return;
      }
    });

    // Click on fullscreen button overlay - fixed click detection
    document.addEventListener("click", (e) => {
      const container = e.target.closest(".product-image-magnifier-container");
      if (container) {
        const rect = container.getBoundingClientRect();
        const clickX = e.clientX;
        const clickY = e.clientY;

        // Check if click is in the top-right corner (fullscreen button area)
        if (clickX > rect.right - 50 && clickY < rect.top + 50) {
          e.preventDefault();
          e.stopPropagation();

          const img = container.querySelector(".product-image-magnifier-img");
          if (img) {
            const index = this.findMediaIndex(img);
            this.open(index);
          }
        }
      }
    });

    // Double click handler for magnifier images (in case single click is handled by magnifier)
    document.addEventListener("dblclick", (e) => {
      const thumbnail = e.target.closest(".product-image-magnifier-img");
      if (thumbnail) {
        e.preventDefault();
        const index = this.findMediaIndex(thumbnail);
        this.open(index);
      }
    });

    // Overlay click to close
    this.overlay?.addEventListener("click", (e) => {
      if (e.target === this.overlay) {
        this.close();
      }
    });

    // Keyboard navigation
    document.addEventListener("keydown", (e) => {
      if (!this.overlay?.classList.contains("active")) return;

      this.isKeyboardNav = true;
      this.overlay.classList.add("keyboard-nav");

      switch (e.key) {
        case "Escape":
          this.close();
          break;
        case "ArrowLeft":
          e.preventDefault();
          this.previousSlide();
          break;
        case "ArrowRight":
          e.preventDefault();
          this.nextSlide();
          break;
        case "ArrowUp":
        case "ArrowDown":
          e.preventDefault();
          break;
      }
    });

    // Remove keyboard nav class on mouse use
    document.addEventListener("mousemove", () => {
      if (this.isKeyboardNav) {
        this.isKeyboardNav = false;
        this.overlay?.classList.remove("keyboard-nav");
      }
    });

    // Touch/swipe support for mobile (only on main image, not thumbnails)
    let touchStartX = 0;
    let touchStartY = 0;
    let swipeAllowed = false;

    this.overlay?.addEventListener("touchstart", (e) => {
      // Only allow swipe if touch starts outside the thumbnails
      const target = e.target;
      const isOnThumbnails =
        target.closest && target.closest(".fullscreen-thumbnails");
      swipeAllowed = !isOnThumbnails;
      if (swipeAllowed && e.touches.length === 1) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      } else {
        touchStartX = 0;
        touchStartY = 0;
      }
    });

    this.overlay?.addEventListener("touchend", (e) => {
      if (!swipeAllowed || !touchStartX || !touchStartY) return;

      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      const deltaX = touchStartX - touchEndX;
      const deltaY = touchStartY - touchEndY;

      // Only process horizontal swipes
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
        if (deltaX > 0) {
          this.nextSlide();
        } else {
          this.previousSlide();
        }
      }

      touchStartX = 0;
      touchStartY = 0;
      swipeAllowed = false;
    });
  }

  findMediaIndex(element) {
    // Try to find index based on src matching
    const src = element.src || element.querySelector("source")?.src;
    const index = this.mediaItems.findIndex(
      (item) =>
        item.src === src ||
        item.thumbnail === src ||
        item.src.includes(src.split("/").pop()) ||
        src.includes(item.src.split("/").pop())
    );
    return index >= 0 ? index : 0;
  }

  open(index = 0) {
    this.currentIndex = index;
    this.overlay.classList.add("active");
    document.body.style.overflow = "hidden";

    this.updateDisplay();

    // Focus management for accessibility
    this.overlay.focus();
  }

  close() {
    // Pause and remove any playing video or iframe
    const mainImageContainer = this.overlay.querySelector(
      ".fullscreen-main-image"
    );
    if (mainImageContainer) {
      // Pause and remove <video>
      const video = mainImageContainer.querySelector("video");
      if (video) {
        video.pause();
        video.currentTime = 0;
        video.remove();
      }
      // Remove <iframe> (for external videos)
      const iframe = mainImageContainer.querySelector("iframe");
      if (iframe) {
        iframe.remove();
      }
    }
    this.overlay.classList.remove("active");
    document.body.style.overflow = "";
  }

  goToSlide(index) {
    if (index >= 0 && index < this.mediaItems.length) {
      this.currentIndex = index;
      this.updateDisplay();
    }
  }

  nextSlide() {
    this.currentIndex = (this.currentIndex + 1) % this.mediaItems.length;
    this.updateDisplay();
  }

  previousSlide() {
    this.currentIndex =
      (this.currentIndex - 1 + this.mediaItems.length) % this.mediaItems.length;
    this.updateDisplay();
  }

  updateDisplay() {
    const currentItem = this.mediaItems[this.currentIndex];
    if (!currentItem) return;

    // Add loading state
    const mainImageContainer = this.overlay.querySelector(
      ".fullscreen-main-image"
    );
    mainImageContainer.classList.add("loading");

    // Update main image, video, or external video
    // Remove any existing video or iframe
    let video = mainImageContainer.querySelector("video");
    if (video) video.remove();
    let iframe = mainImageContainer.querySelector("iframe");
    if (iframe) iframe.remove();

    if (currentItem.type === "video") {
      this.mainImage.style.display = "none";
      video = document.createElement("video");
      video.controls = true;
      video.autoplay = true;
      video.muted = true;
      video.style.maxWidth = "100%";
      video.style.maxHeight = "100%";
      video.style.borderRadius = "8px";
      video.style.boxShadow = "0 4px 20px rgba(0,0,0,0.3)";
      if (currentItem.thumbnail) video.poster = currentItem.thumbnail;
      // Add all <source> elements for the video
      if (
        Array.isArray(currentItem.sources) &&
        currentItem.sources.length > 0
      ) {
        currentItem.sources.forEach((srcObj) => {
          const source = document.createElement("source");
          source.src = srcObj.src;
          source.type = srcObj.type;
          video.appendChild(source);
        });
        video.src = currentItem.src; // Set to preferred src
      } else if (currentItem.src) {
        const source = document.createElement("source");
        source.src = currentItem.src;
        // Try to guess type from extension
        const ext = currentItem.src.split(".").pop().toLowerCase();
        let mime = "video/mp4";
        if (ext === "webm") mime = "video/webm";
        else if (ext === "ogg" || ext === "ogv") mime = "video/ogg";
        source.type = mime;
        video.appendChild(source);
        video.src = currentItem.src;
      }
      video.style.display = "block";
      mainImageContainer.appendChild(video);
      video.load();
      video.play().catch(() => {});
      mainImageContainer.classList.remove("loading");
    } else if (currentItem.type === "external_video") {
      this.mainImage.style.display = "none";
      iframe = document.createElement("iframe");
      iframe.src = currentItem.src;
      iframe.allowFullscreen = true;
      iframe.setAttribute("frameborder", "0");
      iframe.style.maxWidth = "100%";
      iframe.style.maxHeight = "100%";
      iframe.style.borderRadius = "8px";
      iframe.style.boxShadow = "0 4px 20px rgba(0,0,0,0.3)";
      mainImageContainer.appendChild(iframe);
      mainImageContainer.classList.remove("loading");
    } else {
      this.mainImage.style.display = "block";
      this.mainImage.onload = () => {
        mainImageContainer.classList.remove("loading");
      };
      this.mainImage.src = currentItem.src;
      this.mainImage.alt = currentItem.alt;
    }

    // Update thumbnails
    this.thumbnailsContainer
      .querySelectorAll(".fullscreen-thumbnail")
      .forEach((thumb, index) => {
        thumb.classList.toggle("active", index === this.currentIndex);
        if (index === this.currentIndex && this.isKeyboardNav) {
          thumb.classList.add("focus");
        } else {
          thumb.classList.remove("focus");
        }
      });

    // Update counter
    this.counter.querySelector(".current").textContent = this.currentIndex + 1;

    // Auto-scroll thumbnail into view
    const activeThumbnail = this.thumbnailsContainer.querySelector(
      ".fullscreen-thumbnail.active"
    );
    if (activeThumbnail) {
      activeThumbnail.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  // Only initialize on product pages
  if (document.querySelector(".main-product-template")) {
    const gallery = new FullscreenGallery();
    // Fallback: retry after a delay if no media items found
    if (gallery.mediaItems.length === 0) {
      setTimeout(() => {
        gallery.collectMediaItems();
        gallery.initializeThumbnails();
      }, 1000);
    }
  }
});

// Export for external use
window.FullscreenGallery = FullscreenGallery;
